/**
 * Выбор задачи на новом бэке.
 *
 * Вёрстка повторяет legacy `DTaskLinkDropdown` — это тот же контрол «выбор
 * задачи», и расходиться им нельзя. Своим он стал из-за модели: у legacy id
 * числовые и поиск идёт в `/tasks/list`, здесь UUID и `/v2/tasks?search=`.
 *
 * Поиск серверный: локально фильтровать нельзя — бэк отдаёт максимум сотню
 * задач за раз, и всё, что за пределами этой пачки, иначе не нашлось бы.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2 } from 'lucide-react';
import clsx from 'clsx';

import { useDebounced } from '@/hooks/ui/useDebounced';
import { onAltArrowDownOpen } from '@/features/tasks/task-form-modal/utils';
import { useOutsideClick } from '@/hooks/ui/useOutsideClick';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';

import { fetchTasksV2 } from '../api/tasks-v2';

export interface TaskV2Option {
  id: string;
  title: string;
}

interface Props {
  disabled?: boolean;
  /** Выбранная задача — показывается на кнопке вместо плейсхолдера. */
  selected?: TaskV2Option | null;
  /** Задачи, которых не должно быть в выдаче: текущая и уже связанные. */
  excludeIds?: string[];
  placeholder?: string;
  /** К какому краю прижата панель, чтобы не вылезала за модалку. */
  align?: 'left' | 'right';
  /** Ответ сервера ещё не пришёл — выбор заблокирован. */
  pending?: boolean;
  onSelect: (task: TaskV2Option | null) => void;
}

export function TaskV2Picker({
  disabled,
  selected = null,
  excludeIds = [],
  placeholder = 'Выбор задачи',
  align = 'left',
  pending = false,
  onSelect,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query.trim(), 400);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useOutsideClick({
    enabled: open,
    refs: [btnRef, panelRef],
    onOutside: () => setOpen(false),
  });

  // Пустой запрос — свежие задачи, чтобы панель не открывалась пустой.
  const { data, isFetching } = useQuery({
    queryKey: ['tasks-v2', 'picker', debouncedQuery],
    queryFn: () =>
      fetchTasksV2({
        ...(debouncedQuery ? { search: debouncedQuery } : {}),
        sort: 'updatedAt',
        order: 'desc',
        limit: 50,
      }),
    enabled: open,
    staleTime: 30 * 1000,
  });

  const options = useMemo<TaskV2Option[]>(() => {
    const excluded = new Set(excludeIds);

    return (data?.result ?? [])
      .filter((task) => task.id && !excluded.has(task.id))
      .map((task) => ({
        id: task.id as string,
        title: task.title?.trim() || 'Задача без названия',
      }));
  }, [data, excludeIds]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setQuery('');
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);

    return () => window.clearTimeout(timer);
  }, [open]);

  const label = selected ? selected.title : placeholder;

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled || pending}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onAltArrowDownOpen(open, () => setOpen(true))}
        className={clsx(
          'inline-flex h-[35px] items-center gap-2 rounded-[8.36px] bg-white px-[12px]',
          'shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] border border-black/5',
          (disabled || pending) && 'opacity-50 cursor-not-allowed'
        )}
        aria-expanded={open}
        aria-haspopup="menu"
        title={label}
      >
        <span className="max-w-[180px] truncate text-[14px] tracking-[-0.03em] text-[#14222C] underline">
          {label}
        </span>
        {pending ? (
          <Loader2 className="w-[14px] h-[14px] animate-spin text-[#14222C]" />
        ) : (
          <ChevronDown
            className={clsx(
              'w-[14px] h-[14px] text-[#14222C] transition',
              open && 'rotate-180'
            )}
          />
        )}
      </button>

      {open ? (
        <FloatingPanel
          ref={panelRef}
          anchorRef={wrapRef}
          align={align === 'right' ? 'end' : 'start'}
          sideOffset={8}
          className="w-[380px] max-w-[calc(100vw-48px)] rounded-[12px] border border-black/10 bg-white p-2 shadow-[0_12px_30px_rgba(0,0,0,0.18)]"
          role="menu"
        >
          <div className="flex items-center gap-2 pb-2">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по названию..."
              className="h-[32px] w-full rounded-[10px] border border-black/10 bg-[#F8F7FC] px-3 text-[13px] text-[#14222C] outline-none placeholder:text-black/40 focus:border-[#57B79255]"
            />
            {selected ? (
              <button
                type="button"
                className="h-[32px] shrink-0 rounded-[10px] border border-black/10 px-3 text-[12px] text-black/70 transition hover:bg-black/5"
                onClick={() => {
                  onSelect(null);
                  setOpen(false);
                }}
                title="Снять связь"
              >
                Снять
              </button>
            ) : null}
          </div>

          <div className="max-h-[300px] overflow-auto pr-1">
            {isFetching && options.length === 0 ? (
              <div className="flex items-center gap-2 px-2 py-3 text-[13px] text-black/50">
                <Loader2 className="h-[14px] w-[14px] animate-spin" />
                Ищу...
              </div>
            ) : options.length === 0 ? (
              <div className="px-2 py-3 text-[13px] text-black/50">
                Ничего не найдено
              </div>
            ) : (
              <div className="space-y-1">
                {options.map((task) => {
                  const active = selected?.id === task.id;

                  return (
                    <button
                      key={task.id}
                      type="button"
                      className={clsx(
                        'w-full rounded-[10px] px-2 py-2 text-left transition',
                        active ? 'bg-[#57B7921A]' : 'hover:bg-black/5'
                      )}
                      onClick={() => {
                        onSelect(task);
                        setOpen(false);
                      }}
                      role="menuitem"
                      title={task.title}
                    >
                      <div className="truncate text-[13px] font-medium text-[#14222C]">
                        {task.title}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </FloatingPanel>
      ) : null}
    </div>
  );
}
